// src/pages/menu/sales/DealsKanbanPage.js

import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../../../shared/api/apiClient";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const normalize = (v) =>
  v === null || v === undefined ? "" : String(v).toLowerCase();

const STAGE_PREFIX = "stage:";
const DEAL_PREFIX = "deal:";
const STATUS_PREFIX = "status:";

const stageKey = (stageId) => `${STAGE_PREFIX}${String(stageId)}`;
const dealKey = (dealId) => `${DEAL_PREFIX}${String(dealId)}`;
const statusKey = (status) => `${STATUS_PREFIX}${String(status)}`;

const STAGE_NONE = stageKey("none");

const STATUS_WON = statusKey("won");
const STATUS_LOST = statusKey("lost");
const BOARD_RETRY_DELAYS_MS = [1500, 4000, 8000, 15000, 30000];

const retryAfterMs = (error) => {
  const headerValue = error?.response?.headers?.["retry-after"];
  const responseSeconds = error?.response?.data?.retry_after_seconds;
  const seconds = Number(responseSeconds ?? headerValue);

  if (Number.isFinite(seconds) && seconds > 0) {
    return Math.min(seconds * 1000, 60000);
  }

  if (typeof headerValue === "string") {
    const retryAt = Date.parse(headerValue);
    if (Number.isFinite(retryAt)) {
      return Math.min(Math.max(retryAt - Date.now(), 0), 60000);
    }
  }

  return 0;
};

const isTransientRequestError = (error) => {
  const status = Number(error?.response?.status || 0);
  return (
    !error?.response ||
    status === 408 ||
    status === 425 ||
    status === 429 ||
    status >= 500
  );
};

const parseStageId = (stageKeyStr) => {
  const s = String(stageKeyStr);
  if (!s.startsWith(STAGE_PREFIX)) return null;
  const raw = s.slice(STAGE_PREFIX.length);
  if (raw === "none") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const parseStatus = (statusKeyStr) => {
  const s = String(statusKeyStr);
  if (!s.startsWith(STATUS_PREFIX)) return null;
  const raw = s.slice(STATUS_PREFIX.length);
  if (raw === "won" || raw === "lost") return raw;
  return null;
};

const getDealStatusLabel = (status, t) => {
  switch (status) {
    case "won":
      return t("won", { defaultValue: "Won" });
    case "lost":
      return t("crm_deals_status_lost", { defaultValue: "Lost" });
    case "open":
      return t("open", { defaultValue: "Open" });
    default:
      return status || "—";
  }
};

const DealsKanbanPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [stages, setStages] = useState([]);
  const [deals, setDeals] = useState([]);

  const [containers, setContainers] = useState({});
  const [activeDealKey, setActiveDealKey] = useState(null);

  const activeFromContainerRef = useRef(null);
  const reloadRequestRef = useRef(0);
  const reloadRetryTimerRef = useRef(null);
  const reloadRetryAttemptRef = useRef(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [hideEmpty, setHideEmpty] = useState(false);
  const [density, setDensity] = useState("compact");

  const academyDealId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("academy") !== "1") return "";
    return params.get("deal_id") || "";
  }, [location.search]);

  useEffect(() => {
    if (academyDealId) {
      setDensity("comfortable");
      setHideEmpty(false);
    }
  }, [academyDealId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const fetchStages = async () => {
    const res = await api.get("/pipeline-stages");
    return (res.data || [])
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  };

  const fetchDeals = async () => {
    const res = await api.get("/deals", { params: { skip: 0, limit: 500 } });
    const list = Array.isArray(res.data) ? res.data : [];

    if (!academyDealId) return list;

    const academyDeal = list.find(
      (deal) =>
        String(deal.id) === String(academyDealId) ||
        String(deal.ui_id) === String(academyDealId),
    );

    // A stale Academy id must never break the entire Deals board. If the
    // training record is not visible yet, keep the board loaded and let the
    // workflow refresh reconcile the current entity.
    return academyDeal ? [academyDeal] : list;
  };

  const clearReloadRetry = () => {
    if (reloadRetryTimerRef.current != null) {
      window.clearTimeout(reloadRetryTimerRef.current);
      reloadRetryTimerRef.current = null;
    }
  };

  const scheduleReloadRetry = (errors) => {
    const transientErrors = errors.filter(isTransientRequestError);
    if (!transientErrors.length || reloadRetryTimerRef.current != null) return;

    const attempt = reloadRetryAttemptRef.current;
    if (attempt >= BOARD_RETRY_DELAYS_MS.length) return;

    const serverDelay = transientErrors.reduce(
      (delay, requestError) =>
        Math.max(delay, retryAfterMs(requestError)),
      0,
    );
    const delay = Math.max(
      BOARD_RETRY_DELAYS_MS[attempt],
      serverDelay,
    );

    reloadRetryAttemptRef.current = attempt + 1;
    reloadRetryTimerRef.current = window.setTimeout(() => {
      reloadRetryTimerRef.current = null;
      reloadAll({ background: true });
    }, delay);
  };

  async function reloadAll({ manual = false, background = false } = {}) {
    if (manual) {
      clearReloadRetry();
      reloadRetryAttemptRef.current = 0;
    }

    const requestId = ++reloadRequestRef.current;

    try {
      if (!background || (!stages.length && !deals.length)) {
        setLoading(true);
      }
      if (!background) setError(null);

      const [stagesResult, dealsResult] = await Promise.allSettled([
        fetchStages(),
        fetchDeals(),
      ]);

      if (requestId !== reloadRequestRef.current) return;

      const failures = [];

      if (stagesResult.status === "fulfilled") {
        setStages(stagesResult.value);
      } else {
        failures.push(stagesResult.reason);
      }

      if (dealsResult.status === "fulfilled") {
        setDeals(dealsResult.value);
      } else {
        failures.push(dealsResult.reason);
      }

      if (!failures.length) {
        clearReloadRetry();
        reloadRetryAttemptRef.current = 0;
        setError(null);
        return;
      }

      failures.forEach((loadError) => console.error(loadError));
      const detail = failures
        .map((loadError) => loadError?.response?.data?.detail)
        .find((value) => typeof value === "string");
      setError(
        detail ||
          t("crm_deals_failed_load_data_retrying", {
            defaultValue:
              "Не удалось загрузить часть данных. Повторяем автоматически…",
          }),
      );
      scheduleReloadRetry(failures);
    } finally {
      if (requestId === reloadRequestRef.current) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    reloadAll();
    return () => {
      reloadRequestRef.current += 1;
      clearReloadRetry();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academyDealId]);

  const stageNameById = useMemo(() => {
    const map = {};
    for (const s of stages) map[String(s.id)] = s.name;
    return map;
  }, [stages]);

  const dealByKey = useMemo(() => {
    const map = {};
    for (const d of deals) map[dealKey(d.id)] = d;
    return map;
  }, [deals]);

  const hiddenStageIds = useMemo(() => {
    const ids = new Set();
    for (const s of stages) {
      const n = normalize(s?.name);
      if (n === "won" || n === "lost") ids.add(Number(s.id));
    }
    return ids;
  }, [stages]);

  const academyTargetColumnKey = useMemo(() => {
    if (!academyDealId) return "";

    const academyDeal = deals.find(
      (deal) =>
        String(deal.id) === String(academyDealId) ||
        String(deal.ui_id) === String(academyDealId),
    );
    if (!academyDeal) return "";
    if (normalize(academyDeal.status) === "won") return "";

    const workingStages = stages
      .filter((stage) => !hiddenStageIds.has(Number(stage.id)))
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    if (!workingStages.length) return "";

    const currentIndex = workingStages.findIndex(
      (stage) => String(stage.id) === String(academyDeal.stage_id),
    );
    const qualifiedStage = workingStages.find(
      (stage) => normalize(stage?.name) === "qualified",
    );
    if (qualifiedStage) {
      return String(qualifiedStage.id) === String(academyDeal.stage_id)
        ? STATUS_WON
        : stageKey(qualifiedStage.id);
    }

    const targetStage =
      currentIndex < 0
        ? workingStages[0]
        : workingStages[currentIndex + 1] ||
          workingStages.find(
            (stage) => String(stage.id) !== String(academyDeal.stage_id),
          );

    return targetStage ? stageKey(targetStage.id) : "";
  }, [academyDealId, deals, hiddenStageIds, stages]);

  const filteredDealKeys = useMemo(() => {
    if (academyDealId) {
      return new Set(
        deals
          .filter(
            (deal) =>
              String(deal.id) === String(academyDealId) ||
              String(deal.ui_id) === String(academyDealId),
          )
          .map((deal) => dealKey(deal.id)),
      );
    }

    const q = searchQuery.trim().toLowerCase();
    if (!q) return new Set(deals.map((d) => dealKey(d.id)));

    const keys = new Set();
    for (const d of deals) {
      const fields = [
        d.id,
        d.ui_id,
        d.title,
        d.amount,
        d.status,
        d.company_id,
        d.contact_ui_id,
        d.owner_id,
        d.stage_id,
        d.stage_id ? stageNameById[String(d.stage_id)] || "" : "",
        d.created_at ? new Date(d.created_at).toLocaleString() : "",
      ];
      if (fields.some((f) => normalize(f).includes(q))) {
        keys.add(dealKey(d.id));
      }
    }
    return keys;
  }, [academyDealId, deals, searchQuery, stageNameById]);

  useEffect(() => {
    const next = {};

    const visibleStages = stages.filter(
      (s) => !hiddenStageIds.has(Number(s.id)),
    );
    const stageKeys = visibleStages.map((s) => stageKey(s.id));

    next[STAGE_NONE] = [];
    for (const sk of stageKeys) next[sk] = [];

    next[STATUS_WON] = [];
    next[STATUS_LOST] = [];

    const list = deals.slice().sort((a, b) => (b.ui_id ?? 0) - (a.ui_id ?? 0));

    for (const d of list) {
      const dk = dealKey(d.id);
      if (!filteredDealKeys.has(dk)) continue;

      if (d.status === "won") {
        next[STATUS_WON].push(dk);
        continue;
      }

      if (d.status === "lost") {
        next[STATUS_LOST].push(dk);
        continue;
      }

      const sid = d.stage_id != null ? Number(d.stage_id) : null;
      if (sid != null && hiddenStageIds.has(sid)) {
        next[STAGE_NONE].push(dk);
        continue;
      }

      const sk = d.stage_id != null ? stageKey(d.stage_id) : STAGE_NONE;
      if (!next[sk]) next[sk] = [];
      next[sk].push(dk);
    }

    setContainers(next);
  }, [deals, stages, filteredDealKeys, hiddenStageIds]);

  const findContainerOfDealKey = (dk) => {
    const did = String(dk);
    for (const [cid, ids] of Object.entries(containers)) {
      if (ids.includes(did)) return cid;
    }
    return null;
  };

  const requestCompletionSummary = (deal, nextStatus) => {
    const existingSummary = String(deal?.work_summary || "").trim();
    if (existingSummary.length >= 3) return existingSummary;

    if (academyDealId) {
      return nextStatus === "won"
        ? t("academy_deal_completed_won_summary")
        : t("academy_deal_completed_summary");
    }

    const enteredSummary = window.prompt(
      t("crm_deals_completion_summary_prompt", {
        defaultValue:
          "Кратко опишите результат работы перед завершением сделки:",
      }),
      existingSummary,
    );
    if (enteredSummary === null) return null;

    const summary = enteredSummary.trim();
    if (summary.length < 3) {
      window.alert(
        t("work_summary_required", {
          defaultValue: "Укажите результат работы — минимум 3 символа.",
        }),
      );
      return null;
    }

    return summary;
  };

  const updateDealOnServer = async (deal, patch) => {
    const payload = {
      title: deal.title,
      amount: deal.amount,
      status: patch.status ?? deal.status,
      stage_id: Object.prototype.hasOwnProperty.call(patch, "stage_id")
        ? patch.stage_id
        : (deal.stage_id ?? null),
    };

    if (Object.prototype.hasOwnProperty.call(patch, "work_summary")) {
      payload.work_summary = patch.work_summary;
    }

    await api.put(`/deals/${deal.id}`, payload);
  };

  const updateDealStatus = async (deal, newStatus) => {
    if (!deal) return;
    if (saving) return;
    if (deal.status === newStatus) return;

    const confirmed =
      window.confirm(
        t("confirm_change_deal_status", {
          id: deal.ui_id,
          title: deal.title,
          status: getDealStatusLabel(newStatus, t),
          defaultValue:
            'Change status of deal #{{id}} "{{title}}" to "{{status}}"?',
        }),
      ) || false;

    if (!confirmed) return;

    const patch = { status: newStatus };
    if (newStatus === "won" || newStatus === "lost") {
      const workSummary = requestCompletionSummary(deal, newStatus);
      if (!workSummary) return;
      patch.work_summary = workSummary;
    }

    try {
      setSaving(true);
      setError(null);

      await updateDealOnServer(deal, patch);

      setDeals((prev) =>
        prev.map((d) =>
          d.id === deal.id
            ? {
                ...d,
                status: newStatus,
                work_summary: patch.work_summary ?? d.work_summary,
              }
            : d,
        ),
      );
      window.dispatchEvent(new Event("velsoft:academy-workflow-refresh"));
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.detail ||
          t("crm_deals_failed_update_status", {
            defaultValue: "Failed to update deal status",
          }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (event) => {
    const id = String(event.active.id);
    if (!id.startsWith(DEAL_PREFIX)) return;

    activeFromContainerRef.current = findContainerOfDealKey(id);
    setActiveDealKey(id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);

    if (!activeIdStr.startsWith(DEAL_PREFIX)) return;

    const activeContainer = findContainerOfDealKey(activeIdStr);
    const overContainer =
      overIdStr.startsWith(STAGE_PREFIX) || overIdStr.startsWith(STATUS_PREFIX)
        ? overIdStr
        : findContainerOfDealKey(overIdStr);

    if (!activeContainer || !overContainer) return;
    if (activeContainer === overContainer) return;

    setContainers((prev) => {
      const next = { ...prev };
      next[activeContainer] = (next[activeContainer] || []).filter(
        (x) => x !== activeIdStr,
      );
      next[overContainer] = [...(next[overContainer] || []), activeIdStr];
      return next;
    });
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    const activeIdStr = String(active?.id);
    const overIdStr = String(over?.id);

    const startedFrom = activeFromContainerRef.current;
    activeFromContainerRef.current = null;
    setActiveDealKey(null);

    if (!over) return;
    if (!activeIdStr.startsWith(DEAL_PREFIX)) return;

    const deal = dealByKey[activeIdStr];
    if (!deal) return;

    const toContainer =
      overIdStr.startsWith(STAGE_PREFIX) || overIdStr.startsWith(STATUS_PREFIX)
        ? overIdStr
        : findContainerOfDealKey(overIdStr);

    if (!startedFrom || !toContainer) return;

    if (startedFrom === toContainer) {
      const ids = containers[startedFrom] || [];
      const oldIndex = ids.indexOf(activeIdStr);
      const newIndex = ids.indexOf(overIdStr);

      if (
        overIdStr?.startsWith(DEAL_PREFIX) &&
        oldIndex !== -1 &&
        newIndex !== -1 &&
        oldIndex !== newIndex
      ) {
        setContainers((prev) => ({
          ...prev,
          [startedFrom]: arrayMove(prev[startedFrom], oldIndex, newIndex),
        }));
      }

      return;
    }

    try {
      setSaving(true);
      setError(null);

      const toStatus = parseStatus(toContainer);
      const toStageId = toContainer.startsWith(STAGE_PREFIX)
        ? parseStageId(toContainer)
        : null;

      let patch =
        toStatus === "won"
          ? { status: "won" }
          : toStatus === "lost"
            ? { status: "lost" }
            : { status: "open", stage_id: toStageId };

      if (toStatus === "won" || toStatus === "lost") {
        const workSummary = requestCompletionSummary(deal, toStatus);
        if (!workSummary) {
          setDeals((prev) => prev.slice());
          return;
        }
        patch = { ...patch, work_summary: workSummary };
      }

      await updateDealOnServer(deal, patch);

      setDeals((prev) =>
        prev.map((d) => {
          if (d.id !== deal.id) return d;
          return {
            ...d,
            status: patch.status ?? d.status,
            stage_id: Object.prototype.hasOwnProperty.call(patch, "stage_id")
              ? patch.stage_id
              : d.stage_id,
            work_summary: patch.work_summary ?? d.work_summary,
          };
        }),
      );
      window.dispatchEvent(new Event("velsoft:academy-workflow-refresh"));
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.detail ||
          t("crm_deals_failed_move_deal", {
            defaultValue: "Failed to move deal",
          }),
      );
      setDeals((prev) => prev.slice());
      if (isTransientRequestError(e)) {
        scheduleReloadRetry([e]);
      } else {
        await reloadAll({ background: true });
      }
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => {
    const ordered = stages
      .filter((s) => !hiddenStageIds.has(Number(s.id)))
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    return [
      {
        id: STAGE_NONE,
        title: t("crm_deals_no_stage", { defaultValue: "No stage" }),
      },
      ...ordered.map((s) => ({
        id: stageKey(s.id),
        title: s.name,
      })),
      { id: STATUS_WON, title: t("won", { defaultValue: "Won" }) },
      {
        id: STATUS_LOST,
        title: t("crm_deals_status_lost", { defaultValue: "Lost" }),
      },
    ];
  }, [stages, t, hiddenStageIds]);

  const visibleColumns = useMemo(() => {
    if (!hideEmpty) return columns;
    return columns.filter((col) => (containers[col.id] || []).length > 0);
  }, [columns, containers, hideEmpty]);

  const activeDeal = activeDealKey ? dealByKey[String(activeDealKey)] : null;

  const handleRefresh = async () => {
    setSearchQuery("");
    await reloadAll({ manual: true });
  };

  const disableActions = loading || saving;

  return (
    <div data-academy="deals-kanban" className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
        <div>
          <h2 className="mb-1">
            {t("sales_deals", { defaultValue: "Deals" })}
          </h2>
          <div className="text-muted" style={{ fontSize: 13 }}>
            {saving ? t("saving", { defaultValue: "Saving..." }) : ""}
          </div>
        </div>

        <div className="d-flex gap-2 align-items-center flex-wrap">
          <div
            className="btn-group"
            role="group"
            aria-label={t("crm_deals_density", { defaultValue: "Density" })}
          >
            <button
              type="button"
              className={`btn btn-sm ${
                density === "comfortable"
                  ? "btn-primary"
                  : "btn-outline-primary"
              }`}
              onClick={() => setDensity("comfortable")}
              disabled={disableActions}
            >
              {t("crm_deals_density_comfortable", {
                defaultValue: "Comfortable",
              })}
            </button>

            <button
              type="button"
              className={`btn btn-sm ${
                density === "compact" ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setDensity("compact")}
              disabled={disableActions}
            >
              {t("crm_deals_density_compact", { defaultValue: "Compact" })}
            </button>
          </div>

          <button
            type="button"
            className={`btn btn-sm ${
              hideEmpty ? "btn-secondary" : "btn-outline-secondary"
            }`}
            onClick={() => setHideEmpty((v) => !v)}
            disabled={disableActions}
            title={t("crm_deals_hide_empty_title", {
              defaultValue: "Hide columns with 0 deals",
            })}
          >
            {t("crm_deals_hide_empty", { defaultValue: "Hide empty" })}
          </button>

          <input
            className="form-control form-control-sm"
            style={{ width: 260 }}
            placeholder={t("search", { defaultValue: "Search" })}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            disabled={disableActions}
          />

          <button
            data-academy="deals-refresh"
            className="btn btn-outline-secondary"
            onClick={handleRefresh}
            disabled={disableActions}
          >
            {loading
              ? t("loading", { defaultValue: "Loading" }) + "..."
              : t("refresh", { defaultValue: "Refresh" })}
          </button>
        </div>
      </div>

      {error && (
        <div
          data-academy="deals-load-error"
          className="alert alert-danger py-2"
        >
          {error}
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="row g-3 row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-5">
          {visibleColumns.map((col) => (
            <div key={col.id} className="col">
              <KanbanColumn
                id={col.id}
                title={col.title}
                dealKeys={containers[col.id] || []}
                dealByKey={dealByKey}
                disabled={disableActions}
                onWon={(deal) => updateDealStatus(deal, "won")}
                onLost={(deal) => updateDealStatus(deal, "lost")}
                onCreateTask={(deal) =>
                  navigate("/menu/sales/tasks", {
                    state: {
                      deal_id: deal.id,
                      contact_ui_ids: deal.contact_ui_id
                        ? [deal.contact_ui_id]
                        : [],
                      default_title: deal.title || undefined,
                    },
                  })
                }
                density={density}
                academyTarget={
                  Boolean(academyTargetColumnKey) &&
                  String(col.id) === String(academyTargetColumnKey)
                }
              />
            </div>
          ))}
        </div>

        <DragOverlay zIndex={2147482995}>
          {activeDeal ? <DealCardOverlay deal={activeDeal} /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default DealsKanbanPage;

const KanbanColumn = ({
  id,
  title,
  dealKeys,
  dealByKey,
  disabled,
  onWon,
  onLost,
  onCreateTask,
  density,
  academyTarget,
}) => {
  const { t } = useTranslation();

  const { setNodeRef, isOver } = useDroppable({
    id: String(id),
    disabled: false,
  });

  const listMaxHeight = density === "compact" ? 230 : 290;

  return (
    <div
      data-academy="deal-stage-column"
      data-academy-target={academyTarget ? "true" : "false"}
      data-academy-stage-name={title}
      data-academy-stage-code={normalize(title)}
      className="card"
      style={{
        borderRadius: 14,
        overflow: "hidden",
        outline: isOver ? "2px solid rgba(13,110,253,0.45)" : "none",
      }}
    >
      <div
        className="card-header d-flex justify-content-between align-items-center"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          backdropFilter: "blur(6px)",
        }}
      >
        <div style={{ fontWeight: 700 }}>{title}</div>
        <span className="badge bg-secondary">{dealKeys.length}</span>
      </div>

      <div className="card-body" style={{ padding: 10 }}>
        <div
          ref={setNodeRef}
          style={{
            maxHeight: listMaxHeight,
            overflowY: "auto",
            paddingRight: 6,
          }}
        >
          <SortableContext
            items={dealKeys}
            strategy={verticalListSortingStrategy}
          >
            <div
              style={{
                minHeight: 60,
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {dealKeys.length === 0 ? (
                <div
                  style={{
                    border: "1px dashed rgba(255,255,255,0.18)",
                    borderRadius: 12,
                    padding: "14px 10px",
                    textAlign: "center",
                    opacity: 0.75,
                    fontSize: 12,
                  }}
                >
                  {disabled
                    ? "…"
                    : t("crm_deals_drop_here", {
                        defaultValue: "Drop here",
                      })}
                </div>
              ) : null}

              {dealKeys.map((dk) => {
                const deal = dealByKey[String(dk)];
                if (!deal) return null;

                return (
                  <DealCard
                    key={String(dk)}
                    id={String(dk)}
                    deal={deal}
                    disabled={disabled}
                    onWon={() => onWon(deal)}
                    onLost={() => onLost(deal)}
                    onCreateTask={() => onCreateTask(deal)}
                    density={density}
                  />
                );
              })}
            </div>
          </SortableContext>
        </div>
      </div>
    </div>
  );
};

const DealCard = ({
  id,
  deal,
  disabled,
  onWon,
  onLost,
  onCreateTask,
  density,
}) => {
  const { t } = useTranslation();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const [hovered, setHovered] = useState(false);

  const isCompact = density === "compact";
  const showActions = density === "comfortable" || hovered;

  const isWon = deal.status === "won";
  const isLost = deal.status === "lost";

  const borderColor = isWon
    ? "rgba(25, 135, 84, 0.85)"
    : isLost
      ? "rgba(220, 53, 69, 0.85)"
      : "rgba(255,255,255,0.10)";

  const bgColor = isWon
    ? "rgba(25, 135, 84, 0.10)"
    : isLost
      ? "rgba(220, 53, 69, 0.10)"
      : "transparent";

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
    cursor: disabled ? "not-allowed" : "grab",
    border: `1px solid ${borderColor}`,
    background: bgColor,
    userSelect: "none",
  };

  const statusBadgeClass = isWon
    ? "badge bg-success"
    : isLost
      ? "badge bg-danger"
      : "badge bg-light text-dark";

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const tinyBtnStyle = { padding: "2px 6px", fontSize: 12, lineHeight: 1.1 };

  return (
    <div
      data-academy="deal-card"
      data-academy-entity-title={deal.title}
      data-academy-entity-id={deal.id}
      data-academy-entity-ui-id={deal.ui_id}
      ref={setNodeRef}
      style={style}
      className="card shadow-sm"
      {...(!disabled ? { ...attributes, ...listeners } : {})}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="button"
      aria-label={t("crm_deals_card_aria", {
        defaultValue: "Deal {{id}}",
        id: deal.ui_id,
      })}
      title={
        disabled
          ? ""
          : t("crm_deals_drag_to_move", { defaultValue: "Drag to move" })
      }
    >
      <div className={`card-body ${isCompact ? "p-1" : "p-2"}`}>
        <div className="d-flex justify-content-between align-items-start gap-2">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              className="fw-semibold"
              style={{
                fontSize: isCompact ? 13 : 14,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={`#${deal.ui_id} ${deal.title}`}
            >
              #{deal.ui_id} {deal.title}
            </div>

            <div
              className="text-muted"
              style={{ fontSize: isCompact ? 11 : 12, lineHeight: 1.25 }}
            >
              {isCompact ? (
                <>
                  ${deal.amount ?? "-"} • C:{deal.company_id ?? "-"} • P:
                  {deal.contact_ui_id ?? "-"}
                </>
              ) : (
                <>
                  {t("sales_deals_amount", { defaultValue: "Amount" })}:{" "}
                  {deal.amount ?? "-"} ·{" "}
                  {t("sales_contacts_company", { defaultValue: "Company" })}:{" "}
                  {deal.company_id ?? "-"} ·{" "}
                  {t("sales_deals_contact", { defaultValue: "Contact" })}:{" "}
                  {deal.contact_ui_id ?? "-"}
                </>
              )}
            </div>
          </div>

          <div className="d-flex align-items-start gap-2">
            <span className={statusBadgeClass} style={{ whiteSpace: "nowrap" }}>
              {getDealStatusLabel(deal.status, t)}
            </span>

            <span
              className="btn btn-sm btn-outline-primary disabled"
              aria-hidden="true"
              style={{
                lineHeight: 1,
                padding: "0.20rem 0.40rem",
                opacity: 0.55,
                pointerEvents: "none",
              }}
              title={t("crm_deals_drag", { defaultValue: "Drag" })}
            >
              ☰
            </span>
          </div>
        </div>

        {showActions ? (
          <div className="d-flex align-items-center gap-2 mt-2">
            <button
              data-academy="deal-won"
              data-academy-entity-title={deal.title}
              className="btn btn-success btn-sm"
              style={tinyBtnStyle}
              onPointerDown={stop}
              onMouseDown={stop}
              onClick={(e) => {
                stop(e);
                onWon();
              }}
              disabled={disabled || isWon}
              type="button"
              title={t("deal_mark_as_won", { defaultValue: "Mark won" })}
            >
              ✓
            </button>

            <button
              className="btn btn-danger btn-sm"
              style={tinyBtnStyle}
              onPointerDown={stop}
              onMouseDown={stop}
              onClick={(e) => {
                stop(e);
                onLost();
              }}
              disabled={disabled || isLost}
              type="button"
              title={t("deal_mark_as_lost", { defaultValue: "Mark lost" })}
            >
              ✕
            </button>

            {density === "comfortable" && (
              <button
                className="btn btn-outline-secondary btn-sm"
                style={tinyBtnStyle}
                onPointerDown={stop}
                onMouseDown={stop}
                onClick={(e) => {
                  stop(e);
                  onCreateTask();
                }}
                disabled={disabled}
                type="button"
                title={t("create_task", { defaultValue: "Create task" })}
              >
                +{t("sales_tasks_title", { defaultValue: "task" })}
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const DealCardOverlay = ({ deal }) => {
  const { t } = useTranslation();

  const isWon = deal.status === "won";
  const isLost = deal.status === "lost";
  const border = isWon
    ? "rgba(25, 135, 84, 0.85)"
    : isLost
      ? "rgba(220, 53, 69, 0.85)"
      : "rgba(255,255,255,0.18)";

  return (
    <div
      className="card shadow"
      style={{
        width: 340,
        border: `1px solid ${border}`,
        boxShadow:
          "0 18px 44px rgba(0, 0, 0, 0.55), 0 0 24px rgba(91, 140, 255, 0.24)",
        pointerEvents: "none",
      }}
    >
      <div className="card-body p-2">
        <div className="fw-semibold" style={{ fontSize: 14 }}>
          #{deal.ui_id} {deal.title}
        </div>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {t("sales_deals_amount", { defaultValue: "Amount" })}:{" "}
          {deal.amount ?? "-"}
        </div>
      </div>
    </div>
  );
};
